import clsx from 'clsx'
import { forwardRef, type ButtonHTMLAttributes, type SVGProps, type ComponentType, type ReactNode } from 'react'

export type SelectButtonColor = 'neutral' | 'blue'

export interface SelectButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual color variant. `neutral` for unselected; `blue` for active/selected. */
  color?: SelectButtonColor
  /** Optional leading icon rendered before the label. */
  leadingIcon?: ComponentType<SVGProps<SVGSVGElement>>
  /** Optional trailing icon rendered after the label (e.g. ChevronDown for dropdowns). */
  trailingIcon?: ComponentType<SVGProps<SVGSVGElement>>
  /** The label text to display. */
  children?: ReactNode
  /** When true, hides the label via CSS container query at ≤400px. Requires an ancestor with container-type: inline-size. */
  responsive?: boolean
}

const colorStyles: Record<SelectButtonColor, string> = {
  neutral: clsx('border-brand-neutral-300 bg-white text-brand-neutral-600', 'hover:bg-brand-neutral-100', 'disabled:opacity-50 disabled:cursor-not-allowed'),
  blue: clsx('border-brand-200 bg-brand-50 text-brand-500', 'hover:bg-brand-100 hover:border-brand-300', 'disabled:opacity-50 disabled:cursor-not-allowed'),
}

export const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>(function SelectButton(
  { color = 'neutral', leadingIcon: LeadingIcon, trailingIcon: TrailingIcon, children, className, responsive, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      data-ds-component="SelectButton"
      className={clsx(
        'inline-flex h-6 items-center gap-1 overflow-hidden rounded border px-2 text-xs font-medium leading-4',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
        colorStyles[color],
        className
      )}
      {...rest}
    >
      {LeadingIcon && <LeadingIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      {children && <span className={clsx('whitespace-nowrap', responsive && '[@container(max-width:400px)]:hidden')}>{children}</span>}
      {TrailingIcon && <TrailingIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
    </button>
  )
})
